import { Injectable } from '@angular/core';

import { LogService } from './log.service';

import { GALoginMethod } from 'src/app/models';

import * as Sentry from '@sentry/browser';

import { environment } from '../../environments/environment';

import mixpanel from 'mixpanel-browser';
declare var gtag: any;

export enum ANALYTICS_EVENTS {
  listEdited = 'list_details_edited',
  listCreated = 'list_created',
  projectEdited = 'project_details_edited',
  projectCreated = 'project_created',
  teamInvitedUser = 'team_invited_user',
  contractorDeleted = 'contractor_delete',
  contractorCreated = 'contractor_created',
  contractorEdited = 'contractor_edit',
  viewStripeBillingPortal = 'view_stripe_billing_portal',
  loginRegisterSkipped = 'register-skipped',
  emailAccountCreated = 'email_account_created',
  defectEdited = 'defect_edited',
  defectCreated = 'defect_created',
  defectViewShowPanel = 'defect_view_show_panel',
  defectViewShowModal = 'defect_view_show_modal',
  signUp = 'owner_sign_up',
}

/** Firebase Analytics service */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  /** Default currency */
  private readonly currency: 'AUD' = 'AUD';
  /** Default unit cost */
  private readonly unitCost: number = 19;
  // private readonly webAppCombinedGa4: string = "G-ZN5NYCEP0C";
  /** @ignore */
  constructor(private log: LogService) {}

  /**
   * Init gtag tracker in app.component
   */
  init(tag?: string): void {
    try {
      const { ga4measurementTag } = environment;
      gtag('config', tag ?? ga4measurementTag, {
        send_page_view: false,
        allow_enhanced_conversions: true,
      });

      gtag('consent', 'update', {
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        ad_storage: 'granted',
        analytics_storage: 'granted',
      });
    } catch (ex) {
      //
    }
  }

  /**
   * Sets the Enhanced Analytics email parameter
   * @param email
   */
  identifyUser(email: string): void {
    if (!environment.production) {
      console.log('Google Analytics - Set User', email);
    }

    try {
      gtag('set', 'user_data', { email });
    } catch (ex) {
      //
    }
  }

  /**
   * Generic page view event
   * > Also sets the page title `document.title = pageTitle`
   * @param pageTitle
   * @param pageRoute page path WITHOUT leading /
   */
  pageView(pageTitle: string, pageRoute: string): void {
    document.title = pageTitle;

    try {
      const { ga4measurementTag } = environment;
      gtag('config', ga4measurementTag, {
        page_title: pageTitle,
        page_location: `https://account.defectwise.com.au/${pageRoute}`, // The full URL is required.
      });

      mixpanel.track('page_view', {
        page_title: pageTitle,
        page_location: `https://account.defectwise.com.au/${pageRoute}`,
      });
    } catch (ex) {
      //
    }

    this.addSentryBreadcrumb(pageRoute);
  }

  /**
   * Sets the screename and screen_view
   * event to the current page
   * @param pageName Name of page being visited
   */
  private addSentryBreadcrumb(pageName: string): void {
    try {
      Sentry.addBreadcrumb({
        category: 'screen_view',
        message: pageName,
        level: 'info',
      });
    } catch (ex) {
      //
    }
  }

  trackCreateDefect(props: { hasAssignment: boolean }): void {
    try {
      mixpanel.track('create_defect', props);
      this.trackCustomEvent(ANALYTICS_EVENTS.defectCreated);
    } catch (ex) {
      console.error(ex);
    }
  }

  /**
   * Sends a custom event to Firebase Analytics
   * @param eventName Event to be posted
   * @param eventProps Details of event
   */
  trackCustomEvent(eventName: ANALYTICS_EVENTS, eventProps?: any) {
    try {
      gtag('event', eventName, eventProps ?? {});
    } catch (ex) {
      //
    }

    try {
      if (eventProps !== undefined) {
        Sentry.addBreadcrumb({
          category: 'custom_event',
          message: eventName,
          level: 'info',
          data: {
            ...(eventProps ?? {}),
          },
        });
      } else {
        Sentry.addBreadcrumb({
          category: 'custom_event',
          message: eventName,
          level: 'info',
        });
      }
    } catch (ex) {
      //
    }
  }

  /**
   * GA4 Login Event `login`
   * - https://developers.google.com/analytics/devguides/collection/ga4/reference/events#login
   * @param method
   */
  login(method: GALoginMethod | string, email: string): void {
    if (!environment.production) {
      console.log('Google Analytics - Set User');
    }
    try {
      gtag('event', 'login', { method });
    } catch (ex) {
      //
    }
  }

  /**
   * GA4 Login Event `sign_up`
   * - https://developers.google.com/analytics/devguides/collection/ga4/reference/events#sign_up
   * @param method
   */
  signUp(method: GALoginMethod, email: string): void {
    try {
      gtag('event', ANALYTICS_EVENTS.signUp, { method });
    } catch (ex) {
      //
    }
    try {
      const { ga4measurementTag } = environment;
      gtag('get', ga4measurementTag, 'gclid', (gclid?: string) => {
        mixpanel.track(ANALYTICS_EVENTS.signUp, {
          sign_up_method: method,
          gclid: gclid,
        });
      });
    } catch (ex) {
      //
    }
  }
}
